import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Clock, MapPin, Volume2, Users, CalendarDays, Target, Sparkles, Plus, Loader2 } from "lucide-react";
import { badgeZaPodrucje } from "@/lib/constants";
import { toast } from "sonner";

export default function ActivityModal({ activity, onClose, onAdd, api }) {
  const [shown, setShown] = useState(null);
  const [uputa, setUputa] = useState("");
  const [adapting, setAdapting] = useState(false);

  useEffect(() => {
    setShown(activity);
    setUputa("");
  }, [activity]);

  const adapt = async () => {
    if (!uputa.trim()) return;
    setAdapting(true);
    try {
      const r = await axios.post(`${api}/ai/adapt`, { aktivnost: shown, uputa });
      setShown(r.data);
      toast.success("Aktivnost prilagođena!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška pri prilagodbi aktivnosti.");
    } finally {
      setAdapting(false);
    }
  };

  if (!shown) return null;
  const a = shown;

  return (
    <Dialog open={!!activity} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl" data-testid="activity-modal">
        <DialogHeader className="text-left">
          <div className="flex flex-wrap gap-1.5 mb-2">
            <span className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-[0.05em] ${badgeZaPodrucje(a.podrucje)}`}>
              {a.podrucje}
            </span>
            {a.ai_generirano && (
              <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2.5 py-1 text-xs font-bold uppercase tracking-[0.05em] text-purple-700">
                <Sparkles className="h-3 w-3" /> AI prilagođeno
              </span>
            )}
          </div>
          <DialogTitle className="font-display text-2xl font-black text-slate-900" data-testid="modal-title">{a.naziv}</DialogTitle>
          <DialogDescription className="text-slate-600">{a.opis}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm font-medium text-slate-600">
          <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-indigo-500" /> {a.trajanje} min</span>
          <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-indigo-500" /> {a.mjesto}</span>
          <span className="flex items-center gap-1.5"><Volume2 className="h-4 w-4 text-indigo-500" /> {a.razina_buke}</span>
          <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-indigo-500" /> Razredi: {a.razredi.map((r) => `${r}.`).join(" ")}</span>
          <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-indigo-500" /> {a.sezona}</span>
        </div>

        <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-4 flex gap-3">
          <Target className="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
          <p className="text-sm text-indigo-900"><span className="font-bold">Cilj:</span> {a.cilj}</p>
        </div>

        <div>
          <p className="text-xs font-bold tracking-[0.08em] uppercase text-slate-400 mb-2">Materijali</p>
          {a.materijali.length === 0 ? (
            <p className="text-sm text-emerald-700 font-semibold">Nisu potrebni materijali</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {a.materijali.map((m, i) => (
                <span key={i} className="rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700">{m}</span>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs font-bold tracking-[0.08em] uppercase text-slate-400 mb-2">Koraci provedbe</p>
          <ol className="space-y-2.5">
            {a.koraci.map((k, i) => (
              <li key={i} className="flex gap-3 text-sm text-slate-700 leading-relaxed">
                <span className="h-6 w-6 shrink-0 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                {k}
              </li>
            ))}
          </ol>
        </div>

        <div className="rounded-xl border border-purple-200 bg-purple-50/60 p-4">
          <p className="flex items-center gap-1.5 text-sm font-bold text-purple-800 mb-2">
            <Sparkles className="h-4 w-4" /> Prilagodi aktivnost AI-jem
          </p>
          <div className="flex gap-2">
            <input
              data-testid="adapt-input"
              value={uputa}
              onChange={(e) => setUputa(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && adapt()}
              placeholder="npr. za manju grupu, bez škara, kraće trajanje…"
              className="flex-1 rounded-lg border border-purple-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <Button data-testid="adapt-btn" onClick={adapt} disabled={adapting || !uputa.trim()}
              className="rounded-lg bg-purple-600 hover:bg-purple-700">
              {adapting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Prilagodi"}
            </Button>
          </div>
        </div>

        <Button data-testid="modal-add-to-plan" onClick={() => onAdd(a)}
          className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 py-6 text-base font-bold">
          <Plus className="h-5 w-5 mr-2" /> Dodaj u plan dana ({a.trajanje} min)
        </Button>
      </DialogContent>
    </Dialog>
  );
}
